import { BulbOutlined, CaretRightOutlined } from '@ant-design/icons'
import { useUpload } from '@web/react-common'
import { useLocalStorageState, useToggle } from 'ahooks'
import { useSize } from 'ahooks'
import { Modal, Tooltip, Typography, message } from 'antd'
import cls from 'classnames'
import { keyBy } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { TextContent } from '@/components'
import { useFileCreateMutation } from '@/generated/graphql'
import {
  LocalFileFragment,
  PostType,
  RequestedPostFragment,
  SectionType,
  useGetGroupQuery,
  useGetViewerJournalProjectsQuery,
  useGetViewerQuery,
} from '@/generated/graphql'

import { PostEditorActions } from './post-editor-actions.component'
import { PostEditorFiles } from './post-editor-files.component'
import { PostEditorHelp } from './post-editor-help.component'
import { PostEditorJournalProjects } from './post-editor-journal-projects.component'
import { PostEditorLinks } from './post-editor-links.component'
import { PostEditorPublish } from './post-editor-publish.component'
import { PostEditorSelectorList } from './post-editor-selector.component'
import { PostEditorTextarea } from './post-editor-textarea.component'
import s from './post-editor.module.less'
import { PostEditorState, emptyPostEditorState, journalTemplate } from './post-editor.state'

interface PostEditorProps {
  post?: RequestedPostFragment // 编辑模式提供
  sectionId?: SectionType
  groupId?: number
  visible: boolean
  isShare?: boolean
  postType?: PostType
  onClose: () => void
  preFiles?: LocalFileFragment[]
}

interface PostEditorInnerProps extends PostEditorProps {
  onChangeMarkdown: (isMarkdown: boolean) => void
}

enum EditorState {
  Share = 'Share',
  Create = 'Create',
  Edit = 'Edit',
}

const TalePostEditorStateVersion = 'TalePostEditorStateVersionV2'

export const PostEditorInner: React.FC<PostEditorInnerProps> = ({
  post,
  sectionId,
  groupId,
  isShare,
  postType = PostType.General,
  onClose,
  onChangeMarkdown,
  preFiles,
}) => {
  const [t] = useTranslation()
  const [visible, { toggle }] = useToggle()
  const [rootHeight, setRootHeight] = React.useState<string | number>('100%')
  const [newGroupId, setGroupId] = React.useState<number | undefined>(groupId)
  const { data: viewerData } = useGetViewerQuery()
  const { data } = useGetViewerJournalProjectsQuery()
  const { data: groupData } = useGetGroupQuery({
    fetchPolicy: 'cache-first',
    skip: !newGroupId,
    variables: {
      groupId: newGroupId!,
      withAdmins: true,
      pagination: {
        first: 1000,
      },
    },
  })
  const [upload] = useFileCreateMutation()

  const editorState = isShare ? EditorState.Share : post ? EditorState.Edit : EditorState.Create

  const group = groupData?.group

  const [state, setState] = (() => {
    switch (editorState) {
      case EditorState.Create:
        return useLocalStorageState<PostEditorState>(TalePostEditorStateVersion, {
          ...emptyPostEditorState,
          postType: postType,
          isMarkdown: postType !== PostType.Forum,
        })

      case EditorState.Edit:
        return React.useState<PostEditorState>(() => ({
          content: post!.content,
          files: post!.files,
          links: keyBy(post!.links, 'url'),
          isMarkdown: post!.isMarkdown,
          isAnonymous: post!.isAnonymous,
          postType: postType,
        }))

      case EditorState.Share:
        return React.useState<PostEditorState>(() => emptyPostEditorState)
    }
  })()

  React.useEffect(() => {
    setState((state: any) => {
      const newState = {
        ...emptyPostEditorState,
        ...state,
        ...(preFiles && { files: preFiles }),
      }

      if (editorState === EditorState.Create) {
        const shouldUpdateMarkdown = [PostType.Forum, PostType.Journal].includes(postType)
        return {
          ...newState,
          postType,
          ...(shouldUpdateMarkdown && {
            isMarkdown: postType === PostType.Journal,
          }),
        }
      }
      return newState
    })
  }, [])

  React.useEffect(() => {
    onChangeMarkdown(state.isMarkdown)
  }, [state.isMarkdown])

  React.useEffect(() => {
    if (state.postType === PostType.Journal) {
      if (!state.content) {
        setState((state: any) => ({
          ...state!,
          content: journalTemplate(data?.viewer.journalProjects || []),
        }))
      }
    } else {
      if (state.content === journalTemplate(data?.viewer.journalProjects || [])) {
        setState((state: any) => ({ ...state!, content: '' }))
      }
    }
  }, [state.postType])

  const {
    uploadClickRef,
    uploadDropRef: rootRef,
    uploadPasteRef: textareaRef,
  } = useUpload(
    {
      upload: async (file) => {
        const { data } = await upload({
          variables: {
            file,
          },
        })
        return data!.fileCreate
      },
      onUploadStart(file) {
        if (file.type.startsWith('video')) {
          !['webm', 'mp4', 'theora'].includes(file.type.split('/')[1].toLowerCase()) &&
            message.warn(t('post.editor.chromeVideoSupport'))
        }
        if (file.type.startsWith('audio')) {
          !['wav', 'mp3', 'vorbis', 'aac'].includes(file.type.split('/')[1].toLowerCase()) &&
            message.warn(t('post.editor.chromeAudioSupport'))
        }
        setState((state: any) => ({
          ...state!,
          files: [...state!.files, file],
        }))
      },
      onUploadProgress(file) {
        setState((state: any) => ({
          ...state!,
          files: state!.files.map((f: any) => (f.fileId === file.fileId ? file : f)),
        }))
      },
      onUploadComplete(file) {
        setState((state: any) => ({
          ...state!,
          files: state!.files.map((f: any) => (f.fileId === file.fileId ? file : f)),
        }))
      },
      onUploadError(file) {
        message.error(t('post.editor.uploadFailed'))
        if (file) {
          setState((state: any) => ({
            ...state!,
            files: state!.files.filter((f: any) => f.fileId !== file.fileId),
          }))
        }
      },
    },
    []
  )

  const preview = state.isMarkdown && (
    <div className={s.preview}>
      <div>
        {t('post.preview')}
        <PostEditorHelp />
      </div>
      <TextContent isMarkdown mentions={[]} content={state.content} />
    </div>
  )

  const isAnonymous = <span>{t('post.editor.anonymous')}</span>
  const action = isShare ? t('post.share') : post ? t('post.edit') : t('post.create')
  const title =
    state.postType === PostType.Forum
      ? t('post.forum.title')
      : state.postType === PostType.Journal
        ? t('post.journal.name')
        : t('post.post')

  const header = (
    <div className={s.header}>
      {action}
      {state.isAnonymous && isAnonymous}
      {title}
      {sectionId && (
        <CaretRightOutlined
          style={{
            fontSize: 14,
            color: '#6A6A6A',
            marginRight: 4,
            marginLeft: 4,
          }}
        />
      )}
      <span
        className={cls(
          s.postSection,
          sectionId === SectionType.NewAgoran && s.section1Color,
          sectionId === SectionType.Entertainment && s.section2Color,
          sectionId === SectionType.Synthesis && s.section3Color
        )}
      >
        {sectionId && t(`section.${sectionId}.title`)}
      </span>
    </div>
  )

  const textArea = (
    <PostEditorTextarea
      textareaRef={textareaRef}
      rootRef={rootRef}
      state={state}
      setState={setState}
      setRootHeight={setRootHeight}
    />
  )
  const attachment = (
    <div className={s.attachmentsContainer}>
      <PostEditorLinks content={state.content} links={state.links} setState={setState} />
      <PostEditorFiles files={state.files} setState={setState} />
    </div>
  )

  const anonymousCount = viewerData!.viewer.anonymousCount
  const hideAnonymous = !!post || postType === PostType.Journal || (group && !group.canPostAnonymously)

  const showGroupSelector = !post || isShare
  const footer = (
    <div className={s.footerContainer}>
      <PostEditorActions
        isEdit={!!post}
        groupId={newGroupId}
        state={state}
        setState={setState}
        anonymousCount={anonymousCount}
        hideAnonymous={hideAnonymous}
        uploadClickRef={uploadClickRef}
      />
      <span>
        <PostEditorPublish
          postId={post?.postId}
          sectionId={sectionId}
          groupId={newGroupId}
          isShare={isShare}
          state={state}
          setState={setState}
          onSubmit={() => onClose()}
        />
        {showGroupSelector && (
          <span className={s.groupSelector} onClick={() => toggle()}>
            {group ? group.name : t('post.editor.myTimeline')}
          </span>
        )}
      </span>
    </div>
  )

  const journalProjects = (
    <div className={s.journalContainer}>
      <PostEditorJournalProjects setState={setState} projects={data?.viewer.journalProjects || []} />
    </div>
  )

  const groupSelectorHeader = (
    <Typography.Title level={4}>
      {isShare ? t('post.editor.chooseShareGroup') : t('post.editor.choosePublishGroup')}
      <Tooltip title={t('impact.tip.publish')}>
        <BulbOutlined style={{ color: '#FFBB5C', marginLeft: 8, fontSize: 16 }} />
      </Tooltip>
    </Typography.Title>
  )

  const groupSelector = (
    <Modal
      visible={visible}
      footer={false}
      className={s.modal}
      onCancel={() => toggle()}
      title={groupSelectorHeader}
      centered
    >
      <PostEditorSelectorList
        postType={post?.postType}
        setGroupId={setGroupId}
        onSelect={() => toggle()}
        viewer={viewerData!.viewer}
        setState={setState}
      />
    </Modal>
  )

  return (
    <div className={s.root} style={{ height: rootHeight }} ref={rootRef as React.RefObject<HTMLDivElement>}>
      <div className={s.dropCover}>
        <span>{t('post.editor.dropAndUpload')}</span>
      </div>
      {groupSelector}
      {preview}
      <div className={s.mainContainer} style={{ width: preview ? '50%' : '100%' }}>
        {header}
        {textArea}
        {attachment}
        {footer}
        {state.postType === PostType.Journal && journalProjects}
      </div>
    </div>
  )
}

export const PostEditor: React.FC<PostEditorProps> = ({ ...props }) => {
  const size = useSize(document.querySelector('body'))
  const [width, setWidth] = React.useState(600)

  // section 下创建周记默认创建到 user timeline
  const groupId = props.sectionId && props.postType === PostType.Journal ? undefined : props.groupId

  const content = (
    <PostEditorInner
      {...props}
      groupId={groupId}
      onChangeMarkdown={(isMarkdown) => setWidth(isMarkdown ? Math.min(1200, size.width!) : 600)}
      onClose={props.onClose}
    />
  )

  return (
    <Modal
      wrapClassName={s.modal}
      visible={props.visible}
      footer={false}
      centered
      onCancel={props.onClose}
      onClose={props.onClose}
      width={width}
      destroyOnClose
    >
      {content}
    </Modal>
  )
}
